import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { GetRootState } from 'js/src/libs/RootState';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';

import { TextPositionRecommendationOptions } from 'js/src/client/types.gen';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { setSlideTextPositionOperation } from 'js/src/luminary/text-editing-operations';

type TextPositionPreset = Exclude<`${TextPositionRecommendationOptions}`, 'auto'>;

// Every preset except 'auto', in enum-declaration order so the emitted enum matches the
// previous schema.
const TEXT_POSITION_PRESET_VALUES = Object.values(TextPositionRecommendationOptions).filter(
    (value) => value !== TextPositionRecommendationOptions.AUTO,
) as [TextPositionPreset, ...TextPositionPreset[]];

export const legacySetTextPositionPresetSchema = z.object({
    scene_index: z.number().describe('The scene index (0, 1, 2, etc.)'),
    text_position_preset: z
        .enum(TEXT_POSITION_PRESET_VALUES)
        .describe(
            `The text position preset (one of ${TEXT_POSITION_PRESET_VALUES.join(',')}). Not all scenes support all presets.`,
        ),
});
export type SetTextPositionPresetArgs = z.infer<typeof legacySetTextPositionPresetSchema>;

export class LegacySetTextPositionPresetTool extends VideoStateTool<
    typeof legacySetTextPositionPresetSchema
> {
    protected override readonly schema = legacySetTextPositionPresetSchema;
    protected override readonly toolName = TOOL_NAMES.setTextPositionPreset;
    protected override readonly toolDescription =
        'Set text position preset for a specific scene. Text position presets are prebuilt configurations for text and fancy caption assets on a scene. This can be used for coarser adjustments than manually overriding asset positions. Each scene only supports a subset of presets: before calling this tool, read the scene\'s "textPositionPresets" field in the current video state and pass one of those values. Do not guess a preset that is not listed for the scene. Remember! This is a high level tool, so it should always be your first choice when adjusting text positions!';

    public override getToolDisplayName(): string {
        return 'Setting text position preset';
    }

    public get statusIcon(): string {
        return 'format_align_center';
    }

    public async execute(args: SetTextPositionPresetArgs, context: ToolExecutionContext) {
        const { scene_index, text_position_preset } = args;
        const { parentActionGroup } = context;

        if (scene_index >= this.luminary.scenes.length) {
            return `Error: scene_index ${scene_index} is out of bounds for the current video`;
        }

        const scene = this.luminary.scenes[scene_index];
        if (!scene) {
            return `Error: No scene found at index ${scene_index}`;
        }

        // Set up operations
        const { setSlideTextPosition } = makeOperationsCallable(
            {
                setSlideTextPositionOperation,
            },
            this.luminary,
            this.dispatch,
            this.getState as GetRootState,
        );

        try {
            setSlideTextPosition(scene.id, text_position_preset, false, parentActionGroup);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : 'Unknown error occurred';
            const errorPayload = {
                error: errorMessage,
                scene_index,
                requested_text_position_preset: text_position_preset,
                valid_text_position_presets: scene.availableTextPositions,
            };
            return {
                result: JSON.stringify(errorPayload),
                isSuccess: false,
            } satisfies EnhancedToolResult;
        }
        return `Success: Set text_position_preset to ${text_position_preset} for scene ${scene_index}`;
    }
}
